import type { FhirResource, Resource } from 'fhir/r4';

/**
 * The primary clinical/document date on a FHIR resource, as a raw ISO string,
 * reading the date field the resource type actually uses. Returns `undefined`
 * for a resource type this doesn't recognize, or one with no date set. There is
 * deliberately no `meta.lastUpdated` fallback — an ingestion timestamp would
 * misrepresent the resource's date.
 *
 * Prefer a model's own date getter (e.g. `DocumentModel.dateCreated`,
 * `ConditionModel.recordedDate`) when you have a typed model; this exists for
 * the case where you hold a raw resource of mixed or unknown type — e.g.
 * resolving a heterogeneous list of references to a single display date. The
 * caller formats the result (with `formatFHIRDate`) or sorts on the ISO value.
 */
export function getResourceDate(resource: Resource | undefined): string | undefined {
  if (!resource) {
    return undefined;
  }
  // `Resource` is the wide base type models expose; narrow to the discriminated
  // union so each case reads its own typed date field.
  const r = resource as FhirResource;
  switch (r.resourceType) {
    case 'Observation':
      return r.effectivePeriod?.start ?? r.effectiveDateTime;
    case 'Condition':
    case 'AllergyIntolerance':
      return r.onsetDateTime ?? r.onsetPeriod?.start ?? r.recordedDate;
    case 'Procedure':
      return r.performedDateTime ?? r.performedPeriod?.start;
    case 'Encounter':
      return r.period?.start;
    case 'DocumentReference':
      return r.date ?? r.content?.[0]?.attachment?.creation;
    case 'DiagnosticReport':
      return r.effectiveDateTime ?? r.effectivePeriod?.start ?? r.issued;
    case 'MedicationStatement':
      return r.effectiveDateTime ?? r.effectivePeriod?.start ?? r.dateAsserted;
    case 'MedicationAdministration':
      return r.effectiveDateTime ?? r.effectivePeriod?.start;
    case 'MedicationRequest':
      return r.authoredOn;
    case 'Immunization':
      return r.occurrenceDateTime;
    default:
      return undefined;
  }
}
